import { PageLimit } from '@/components/TablePersonalizada';
import {
	createLog,
	deleteLog,
	getLogById,
	searchLogs,
	searchLogsByTable,
	searchLogsByTableAndIdRegister,
	updateLog,
} from '@/modules/Log/api';
import {
	CreateLogRequest,
	Log,
	LogResponse,
	LogsResponse,
	UpdateLogRequest,
} from '@/modules/Log/interfaces';
import { useIsAuthenticated } from '@/hooks/useIsAuthenticated';
import {
	useMutation,
	UseMutationOptions,
	useQuery,
	UseQueryOptions,
	useQueryClient,
} from '@tanstack/react-query';

const logQueryKeys = {
	root: ['logs'] as const,
	list: (params: PageLimit) => [...logQueryKeys.root, 'list', params] as const,
	detail: (id: string) => [...logQueryKeys.root, 'detail', id] as const,
	table: (table: string, params: PageLimit) =>
		[...logQueryKeys.root, 'table', table, params] as const,
	tableRecord: (table: string, idRegister: string, params: PageLimit) =>
		[...logQueryKeys.root, 'table-record', table, idRegister, params] as const,
};

type LogsListKey = ReturnType<typeof logQueryKeys.list>;
type LogsTableKey = ReturnType<typeof logQueryKeys.table>;
type LogsTableRecordKey = ReturnType<typeof logQueryKeys.tableRecord>;
type LogDetailKey = ReturnType<typeof logQueryKeys.detail>;

type LogsQueryOptions<TQueryKey extends readonly unknown[]> = Omit<
	UseQueryOptions<LogsResponse, Error, LogsResponse, TQueryKey>,
	'queryKey' | 'queryFn'
>;

type LogDetailQueryOptions = Omit<
	UseQueryOptions<Log, Error, Log, LogDetailKey>,
	'queryKey' | 'queryFn'
>;

export const useLogs = (params: PageLimit, options?: LogsQueryOptions<LogsListKey>) => {
	const isAuthenticated = useIsAuthenticated();
	const { enabled, ...rest } = options ?? {};
	return useQuery({
		queryKey: logQueryKeys.list(params),
		queryFn: () => searchLogs(params),
		enabled: (enabled ?? true) && isAuthenticated,
		...rest,
	});
};

export const useLogsByTable = (
	table: string,
	params: PageLimit,
	options?: LogsQueryOptions<LogsTableKey>,
) => {
	const isAuthenticated = useIsAuthenticated();
	const { enabled, ...rest } = options ?? {};
	return useQuery({
		queryKey: logQueryKeys.table(table, params),
		queryFn: () => searchLogsByTable(table, params),
		enabled: (enabled ?? true) && Boolean(table) && isAuthenticated,
		...rest,
	});
};

export const useLogsByTableAndRecord = (
	table: string,
	idRegister: string,
	params: PageLimit,
	options?: LogsQueryOptions<LogsTableRecordKey>,
) => {
	const isAuthenticated = useIsAuthenticated();
	const { enabled, ...rest } = options ?? {};
	return useQuery({
		queryKey: logQueryKeys.tableRecord(table, idRegister, params),
		queryFn: () => searchLogsByTableAndIdRegister(table, idRegister, params),
		enabled: (enabled ?? true) && Boolean(table) && Boolean(idRegister) && isAuthenticated,
		...rest,
	});
};

export const useLogById = (id: string | null, options?: LogDetailQueryOptions) => {
	const isAuthenticated = useIsAuthenticated();
	const { enabled, ...rest } = options ?? {};
	return useQuery({
		queryKey: logQueryKeys.detail(id || ''),
		queryFn: () => getLogById(id as string),
		enabled: (enabled ?? true) && Boolean(id) && isAuthenticated,
		...rest,
	});
};

type MutationOptions<TData, TVariables> = Omit<
	UseMutationOptions<TData, Error, TVariables>,
	'mutationFn'
>;

export const useCreateLogMutation = (options?: MutationOptions<LogResponse, CreateLogRequest>) => {
	const queryClient = useQueryClient();
	const { onSuccess, ...rest } = options ?? {};
	return useMutation({
		mutationFn: (payload: CreateLogRequest) => createLog(payload),
		onSuccess: (data, variables, context) => {
			onSuccess?.(data, variables, context);
			queryClient.invalidateQueries({ queryKey: logQueryKeys.root });
		},
		...rest,
	});
};

export const useUpdateLogMutation = (
	options?: MutationOptions<LogResponse, { id: string; data: UpdateLogRequest }>,
) => {
	const queryClient = useQueryClient();
	const { onSuccess, ...rest } = options ?? {};
	return useMutation({
		mutationFn: ({ id, data }) => updateLog(id, data),
		onSuccess: (data, variables, context) => {
			onSuccess?.(data, variables, context);
			queryClient.invalidateQueries({ queryKey: logQueryKeys.root });
			queryClient.invalidateQueries({ queryKey: logQueryKeys.detail(variables.id) });
		},
		...rest,
	});
};

export const useDeleteLogMutation = (options?: MutationOptions<{ message: string }, string>) => {
	const queryClient = useQueryClient();
	const { onSuccess, ...rest } = options ?? {};
	return useMutation({
		mutationFn: (id: string) => deleteLog(id),
		onSuccess: (data, variables, context) => {
			onSuccess?.(data, variables, context);
			queryClient.invalidateQueries({ queryKey: logQueryKeys.root });
			queryClient.removeQueries({ queryKey: logQueryKeys.detail(variables) });
		},
		...rest,
	});
};

export { logQueryKeys };
